/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

const isProduction =
	window.Shopify.shop.toLowerCase().includes('sandbox')
		? false
		: window.Shopify.theme.role === 'main'
			? true
			: false;
const PLAYER_URL = isProduction
	? 'https://my.downpour.com'
	: window.themeVariables && window.themeVariables.player && window.themeVariables.player.playerDevUrl
		? window.themeVariables.player.playerDevUrl.trim()
		: 'https://my.downpour.com';

// TODO: This will need some improvements in the future
const API_URL = isProduction
	? 'https://app.downpour.com'
	: 'https://downpour-app--dev.gadget.app';

window.API_URL = API_URL;

const checkCookie = (name) => {
	if (!ShopifyAnalytics.meta.page.customerId && !window.customerId) {
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
	}
};

checkCookie('gadget_shopify_link');

window.getApiToken = async (customerId) => {
	// Check if the token exists in cookies
	const existingToken = getCookie('gadget_shopify_link');
	const storedLoginAsCustomerId = getCookie('gadget_login_as_customer_id');

	// Determine current loginAsCustomer state
	const isCurrentlyLoginAsCustomer = window.loginAsCustomer && window.loginAsCustomerId && window.loginAsCustomerId == customerId && window.customerId != customerId;
	const currentLoginAsCustomerId = isCurrentlyLoginAsCustomer ? String(window.loginAsCustomerId) : null;

	// Check for mismatch: cookie exists but loginAsCustomer state changed
	const hasLoginAsMismatch =
		(storedLoginAsCustomerId && !isCurrentlyLoginAsCustomer) || // Had loginAs, but now it's removed
		(storedLoginAsCustomerId && currentLoginAsCustomerId && storedLoginAsCustomerId !== currentLoginAsCustomerId) || // Different loginAs customer
		(existingToken && !storedLoginAsCustomerId && isCurrentlyLoginAsCustomer); // Token exists but was generated for normal customer, now in loginAs mode

	// Clear token if there's a mismatch
	if (hasLoginAsMismatch && existingToken) {
		window.deleteCookie('gadget_shopify_link');
		window.deleteCookie('gadget_login_as_customer_id');
	}

	if (isCurrentlyLoginAsCustomer) {
		// Generate token for simulated customer
		const token = existingToken && !hasLoginAsMismatch ? existingToken : null;

		if (token) {
			return token;
		}

		try {
			const response = await fetch(`${API_URL}/browser/generate-token`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ customerId }),
			});

			if (!response.ok) {
				throw new Error(`Failed to generate token: ${response.status}`);
			}

			const data = await response.json();

			if (!data || !data.token) {
				throw new Error('Invalid response: Token not found');
			}

			// Save the token and loginAsCustomerId to cookies with 30-day expiration
			setCookie('gadget_shopify_link', data.token, 30);
			setCookie('gadget_login_as_customer_id', String(window.loginAsCustomerId), 30);
			return data.token;
		} catch (error) {
			console.error('Error getting API token:', error);
		}
	} else {
		// Normal customer flow - clear loginAsCustomerId cookie if it exists
		if (storedLoginAsCustomerId) {
			window.deleteCookie('gadget_login_as_customer_id');
		}

		if (existingToken && !hasLoginAsMismatch) {
			return existingToken;
		}

		try {
			const response = await fetch(`${API_URL}/browser/generate-token`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ customerId }),
			});

			if (!response.ok) {
				throw new Error(`Failed to generate token: ${response.status}`);
			}

			const data = await response.json();

			if (!data || !data.token) {
				throw new Error('Invalid response: Token not found');
			}

			// Save the token to cookies with 30-day expiration
			setCookie('gadget_shopify_link', data.token, 30);
			return data.token;
		} catch (error) {
			console.error('Error getting API token:', error);
		}
	}
};

// Helper function to get a cookie by name
const getCookie = (name) => {
	const cookies = document.cookie.split(';');

	for (let cookie of cookies) {
		const [cookieName, cookieValue] = cookie.trim().split('=');

		if (cookieName === name) {
			return cookieValue;
		}
	}
	return null;
};

window.deleteCookie = (name) => {
	document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

// Helper function to set a cookie with expiration
const setCookie = (name, value, daysToExpire) => {
	// set a cookie with the name, value, and expiration date
	const date = new Date();
	date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
	document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
};

window.addEventListener('DOMContentLoaded', (event) => {
	document.addEventListener('click', (event) => {
		const source = event.target;

		if (source.classList.contains('fetch-section-button')) {
			event.preventDefault();
			source.classList.add('is-loading');
			const sectionUrl = source.getAttribute('data-render-section-url');
			const sectionName = source.getAttribute('data-render-section-name');
			const sectionVariantId = source.getAttribute('data-variant-id');

			if (!sectionUrl || !sectionName || !sectionVariantId) {
				return;
			}

			if (source.parentElement.querySelector('[data-external-button-remove]')) {
				source.parentElement.querySelector('[data-external-button-remove]').remove();
			}

			if (document.querySelector(`${sectionName}[data-variant-id="${sectionVariantId}"]`)) {
				document.querySelector(`${sectionName}[data-variant-id="${sectionVariantId}"]`).setAttribute('open', '');
				source.classList.remove('is-loading');
			} else {
				fetch(sectionUrl, {
					headers: {
						'X-Requested-With': 'XMLHttpRequest',
					},
				})
					.then((response) => response.text())
					.then((html) => {
						const content = new DOMParser().parseFromString(html, 'text/html');
						const section = content.querySelector(sectionName);

						if (section) {
							if (document.querySelectorAll('quick-buy-drawer[open]').length > 0 && section.querySelector('.section-inner-content')) {
								const innerSection = section.querySelector('.section-inner-content');
								innerSection.setAttribute('data-external-button-remove', '');

								source.insertAdjacentElement('afterend', innerSection);
							} else {
								document.body.appendChild(section);
							}
						}
					})
					.catch((error) => {
						console.error('Error:', error);
					})
					.finally(() => {
						source.classList.remove('is-loading');
					});
			}
		}
	});
});

if (typeof SearchForm !== 'function') {
	class SearchForm extends HTMLElement {
		constructor() {
			super();

			this.initalPossition = window.innerWidth < 1024 ? 'mobile' : 'desktop';
			this.cachedResults = {};
			this.input = this.querySelector('[data-js-search-input]');
			this.predictiveSearchResults = this.querySelector('[data-js-search-results]');
			this.results = [];
			this.preloader = false;
			this.searchOverlay = document.querySelector('.header__search-bar .search-overlay');
			this.mainHeader = document.querySelector('store-header.header');
			this.mobileParent = document.querySelector('.header__wrapper-search');
			this.desktopParent = document.querySelector('.header__inline-navigation');
			this.searchFormHeaderClose = document.querySelector('.header__search-bar .predictive-search__search-close');
			this.searchFormHeaderSearch = document.querySelector('.header__search-bar .predictive-search__search-icon');
			this.searchBoxTrigger = document.querySelector('.header__icon-wrapper[aria-controls="search-box"]');

			let inputValue = this.input.value;

			this.checkPosition();

			window.addEventListener(
				'resize',
				debounce(() => {
					this.initalPossition = window.innerWidth < 1024 ? 'mobile' : 'desktop';
					this.checkPosition();
				}, 50)
			);

			this.input.addEventListener(
				'keyup',
				debounce((e) => {
					if (this.input.value != inputValue) {
						inputValue = this.input.value;
						this.addOverlay();
						this.getSearchResults(this.input.value.trim());
					} else if (this.input.value == '') {
						this.clearSearchResults();
					}
				}, 150)
			);

			this.input.addEventListener('keydown', (e) => {
				if (this.a11yIndex != -1) {
					if (e.keyCode === window.KEYCODES.UP || e.keyCode === window.KEYCODES.DOWN || e.keyCode === window.KEYCODES.RETURN) {
						e.preventDefault();
					}
				}
			});

			this.input.addEventListener('keyup', (e) => {
				if (e.keyCode === window.KEYCODES.UP) {
					this.navigateThrough('UP');
					return true;
				}

				if (e.keyCode === window.KEYCODES.DOWN) {
					this.navigateThrough('DOWN');
					return true;
				}

				if (e.keyCode === window.KEYCODES.RETURN) {
					if (this.a11yIndex != -1) {
						document.location.href =
							this.results[this.a11yIndex].tagName == 'A'
								? this.results[this.a11yIndex].href
								: this.results[this.a11yIndex].querySelector('a').href;
					}
				}
			});

			this.searchOverlay.addEventListener('click', (e) => {
				if (e.target == this.searchOverlay) {
					this.clearSearchResults();
				}
			});

			this.searchFormHeaderClose
				? this.searchFormHeaderClose.addEventListener('click', (e) => {
					this.clearSearchResults();
				})
				: '';

			this.addEventListener('click', (e) => {
				if (e.target.getAttribute('data-action') == 'send-search') {
					this.querySelector('form').submit();
				}
			});
		}

		checkPosition() {
			if (this.initalPossition == 'desktop') {
				if (this.mobileParent && this.mobileParent.querySelector('search-form')) {
					this.desktopParent.appendChild(this);
					this.classList.remove('header__search-bar--mobile-open');
					this.classList.remove('header__search-bar--mobile-closed');
				}
			} else {
				if (this.desktopParent && this.desktopParent.querySelector('search-form')) {
					this.mobileParent.appendChild(this);
					this.classList.remove('header__search-bar--mobile-open');
					this.classList.add('header__search-bar--mobile-closed');
				}

				if (this.searchBoxTrigger) {
					this.searchBoxTrigger.onclick = (e) => {
						e.preventDefault();
						this.toggleSearchBar();
					};
				}
			}
		}

		toggleSearchBar() {
			this.classList.toggle('header__search-bar--mobile-open');
			this.classList.toggle('header__search-bar--mobile-closed');
		}

		getSearchResults(query = '') {
			this.preloadSearchResults();

			const queryKey = query.replace(' ', '-').toLowerCase();

			if (this.cachedResults[queryKey]) {
				this.renderSearchResults(this.cachedResults[queryKey]);
				return;
			}

			this.a11yIndex = -1;

			if (query != '') {
				fetch(
					`${window.themeVariables.routes.predictiveSearchUrl}?q=${encodeURIComponent(query)}${window.themeVariables.routes.predictiveSearchResults
					}`
				)
					.then((response) => {
						if (!response.ok) {
							var error = new Error(response.status);
							this.clearSearchResults();
							throw error;
						}
						return response.text();
					})
					.then((text) => {
						const results = new DOMParser()
							.parseFromString(text, 'text/html')
							.querySelector('#shopify-section-predictive-search').innerHTML;
						this.cachedResults[queryKey] = results;
						this.renderSearchResults(results);
					})
					.catch((e) => {
						throw e;
					});
			} else {
				this.clearSearchResults();
			}
		}

		clearSearchResults() {
			this.predictiveSearchResults.innerHTML = '';
			this.preloader = false;
			this.results = [];
			this.searchFormHeaderClose ? this.searchFormHeaderClose.setAttribute('hidden', '') : '';
			this.searchFormHeaderSearch ? this.searchFormHeaderSearch.removeAttribute('hidden') : '';

			if (this.searchOverlay) {
				this.searchOverlay = document.querySelector('.header__search-bar .search-overlay');
				this.mainHeader.classList.remove('header-search--opened');
				this.input.value = '';
			}
		}

		addOverlay() {
			if (this.searchOverlay) {
				this.searchOverlay = document.querySelector('.header__search-bar .search-overlay');
				this.mainHeader.classList.add('header-search--opened');
			}
		}

		preloadSearchResults() {
			if (!this.preloader) {
				this.preloader = true;
				this.predictiveSearchResults.innerHTML = `<p class="text--center text--large">${window.themeVariables.strings.searchLoading}</p>`;
			}
		}

		renderSearchResults(results) {
			this.predictiveSearchResults.innerHTML = results;
			this.preloader = false;
			this.results = this.predictiveSearchResults.querySelectorAll('[data-js-search-item]');
			this.searchFormHeaderClose ? this.searchFormHeaderClose.removeAttribute('hidden') : '';
			this.searchFormHeaderSearch ? this.searchFormHeaderSearch.setAttribute('hidden', '') : '';
		}

		navigateThrough(direction) {
			if (this.a11yIndex == -1) {
				this.a11yIndex = 0;
			} else {
				this.results[this.a11yIndex].classList.remove('active');
				if (direction == 'UP') {
					if (this.a11yIndex - 1 >= -1) {
						this.a11yIndex--;
					}
				} else if (direction == 'DOWN') {
					if (this.a11yIndex + 1 < this.results.length) {
						this.a11yIndex++;
					}
				}
			}

			if (this.a11yIndex >= 0) {
				this.results[this.a11yIndex].classList.add('active');
				document.querySelector('[data-js-search-results-holder]').scrollTop = this.results[this.a11yIndex].offsetTop - 200;
			} else {
				document.querySelector('[data-js-search-results-holder]').scrollTop = 0;
			}
		}
	}

	if (typeof customElements.get('search-form') == 'undefined') {
		customElements.define('search-form', SearchForm);
	}
}

if (typeof ScriptExecuter !== 'function') {
	class ScriptExecuter extends HTMLElement {
		constructor() {
			super();

			this.codeToExecute = this.textContent;
		}

		connectedCallback() {
			this.executeScript();
		}

		executeScript() {
			const script = document.createElement('script');
			script.textContent = this.codeToExecute;
			script.type = 'text/javascript';
			script.onload = () => {
				this.remove();
			};

			document.body.appendChild(script);
		}
	}

	if (typeof customElements.get('script-executer') == 'undefined') {
		customElements.define('script-executer', ScriptExecuter);
	}
}

if (typeof BookshelfPlayer !== 'function') {
	class BookshelfPlayer extends HTMLElement {
		constructor() {
			super();
			this.attachEvents = this.attachEvents.bind(this);
			this.onVariantChanged = this.onVariantChanged.bind(this);
			this.render = this.render.bind(this);
			this.bookshelf = [];
			this.variantSKU = null;
			this.showPlayer = false;
			this.bookId = null;
			this.variantTitle = null;
		}

		connectedCallback() {
			this.parseAttributes();
			this.render();
			this.attachEvents();
		}

		parseAttributes() {
			try {
				const bookshelfAttr = this.getAttribute('data-bookshelf');
				this.bookshelf = bookshelfAttr ? JSON.parse(bookshelfAttr) : [];
			} catch (e) {
				this.bookshelf = [];
			}
			this.variantSKU = this.getAttribute('data-variant-sku');
			this.showPlayer = window.themeVariables && window.themeVariables.player;
			this.playButtonText = this.getAttribute('data-play-button-text') || 'Play';
			this.playButtonType = this.getAttribute('data-play-button-type') || 'primary';
			this.playButtonIcon = JSON.parse(this.getAttribute('data-play-button-icon') || '""');
			this.variantTitle = this.getAttribute('data-variant-title');
			this.playerVariantFilterArray = (
				window.themeVariables &&
				window.themeVariables.player &&
				window.themeVariables.player.playerVariantFilterArray
			).map((item) => item.trim().toLowerCase());
		}

		attachEvents() {
			document.addEventListener('variant:changed', this.onVariantChanged);
		}

		disconnectedCallback() {
			document.removeEventListener('variant:changed', this.onVariantChanged);
		}

		onVariantChanged(e) {
			// Our theme dispatches: { variant: { sku, ... } }
			const variant = e.detail && e.detail.variant;
			if (variant && variant.sku) {
				this.variantSKU = String(variant.sku);
				this.variantTitle = variant.title || this.variantTitle;
				this.render();
			}
		}

		bookshelfHasVariant() {
			if (!this.bookshelf || !this.variantSKU) return false;

			return this.bookshelf.some((item) => String(item.sku) === String(this.variantSKU));
		}

		render() {
			this.innerHTML = '';
			const isActive = this.showPlayer && this.bookshelfHasVariant() && this.playerVariantFilterArray.includes(this.variantTitle.toLowerCase());
			const productForm = this.closest('.product-form');

			if (productForm) {
				productForm.classList.toggle('bookshelf-player--active', isActive);
			}
			if (isActive) {
				const player = document.createElement('div');
				player.className = 'bookshelf-player__container';
				player.tabIndex = 0;
				player.setAttribute('role', 'region');
				player.setAttribute('aria-label', 'Audiobook Player');

				const button = document.createElement('button');
				button.type = 'submit';
				button.setAttribute('is', 'loader-button');
				button.setAttribute('data-action', 'play-book');
				button.className = `button button--${this.playButtonType} button--full`;

				if (this.playButtonIcon) {
					const iconWrapper = document.createElement('span');
					iconWrapper.className = 'loader-button__text';
					iconWrapper.innerHTML = this.playButtonIcon || '';
					if (this.playButtonText) {
						iconWrapper.appendChild(document.createTextNode(this.playButtonText));
					}
					button.appendChild(iconWrapper);
				}

				button.addEventListener('click', (e) => {
					e.preventDefault();
					const bookId = this.bookshelf.find((item) => item.sku === this.variantSKU)?.bookId;
					this.bookId = bookId;

					// Dispatch custom event before opening the player
					this.dispatchBookPlayedEvent(bookId);

					this.playBook(this.bookId);
				});

				player.appendChild(button);

				this.appendChild(player);
			}
		}

		dispatchBookPlayedEvent(bookId) {
			// Create and dispatch a custom event when a book is played
			const bookPlayedEvent = new CustomEvent('downpour:book-played', {
				bubbles: true,
				detail: {
					bookId: bookId,
					variantSKU: this.variantSKU,
					status: 'progress', // Set default status when book is played
				},
			});

			// Dispatch the event on the document so it can be caught by React
			document.dispatchEvent(bookPlayedEvent);
		}

		playBook(bookId) {
			const windowName = 'windowPlayer';

			let width, height, playerAlignmentX, playerAlignmentY, urlToPlay;

			if (window.themeVariables && window.themeVariables.player) {
				width = window.themeVariables.player.playerWidthDesktop || 600;
				height = window.themeVariables.player.playerHeightDesktop || 600;
				playerAlignmentX = window.themeVariables.player.playerHorizontalAlignment || 0;
				playerAlignmentY = window.themeVariables.player.playerVerticalAlignment || 0;
				urlToPlay = `${PLAYER_URL}/${bookId}`;

				if (window.innerWidth < 768) {
					const getFullWV = window.innerWidth;
					const getFullHV = window.innerHeight;

					const pvBasedOnWV = (getFullWV * window.themeVariables.player.playerWidthMobile) / 100;
					const pvBasedOnHV = (getFullHV * window.themeVariables.player.playerHeightMobile) / 100;

					width = pvBasedOnWV;
					height = pvBasedOnHV;
				}

				let left = screen.width - width;
				let top = screen.height - height;

				if (window.innerWidth > 768) {
					left = (playerAlignmentX / 100) * screen.width - width / 2;
					top = (playerAlignmentY / 100) * screen.height - height / 2;
				}

				const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=no,scrollbars=no`;

				window.open(urlToPlay, windowName, features);
			}
		}
	}

	if (typeof customElements.get('bookshelf-player') == 'undefined') {
		customElements.define('bookshelf-player', BookshelfPlayer);
	}
}

if (typeof BookshelfPlaceholder !== 'function') {
	class BookshelfPlaceholder extends HTMLElement {
		constructor() {
			super();
			this.attachEvents = this.attachEvents.bind(this);
			this.onVariantChanged = this.onVariantChanged.bind(this);
			this.updateVisibility = this.updateVisibility.bind(this);
			this.bookshelf = [];
			this.variantSKU = null;
		}

		connectedCallback() {
			this.parseAttributes();
			this.updateVisibility();
			this.attachEvents();
		}

		parseAttributes() {
			try {
				const bookshelfAttr = this.getAttribute('data-bookshelf');
				this.bookshelf = bookshelfAttr ? JSON.parse(bookshelfAttr) : [];
			} catch (e) {
				this.bookshelf = [];
			}
			this.variantSKU = this.getAttribute('data-variant-sku');
		}

		attachEvents() {
			document.addEventListener('variant:changed', this.onVariantChanged);
		}

		disconnectedCallback() {
			document.removeEventListener('variant:changed', this.onVariantChanged);
		}

		onVariantChanged(e) {
			const variant = e.detail && e.detail.variant;
			if (variant && variant.sku) {
				this.variantSKU = String(variant.sku);
				this.updateVisibility();
			}
		}

		bookshelfHasVariant() {
			if (!this.bookshelf || !this.variantSKU) return false;

			return this.bookshelf.some((item) => String(item.sku) === String(this.variantSKU));
		}

		updateVisibility() {
			const isInBookshelf = this.bookshelfHasVariant();
			if (isInBookshelf) {
				this.classList.remove('is-hidden');
				const productForm = this.closest('.product-form');

				if (productForm) {
					productForm.classList.add('bookshelf-player--active');
				}
			} else {
				this.classList.add('is-hidden');
				const productForm = this.closest('.product-form');

				if (productForm) {
					productForm.classList.remove('bookshelf-player--active');
				}
			}
		}
	}

	if (typeof customElements.get('bookshelf-placeholder') == 'undefined') {
		customElements.define('bookshelf-placeholder', BookshelfPlaceholder);
	}
}

window.addEventListener('load', () => {
	if (document.querySelector('.shopify-app-block .boost-sd__product-item') && window.themeVariables.quickBuyHTML && window.themeVariables.quickBuyDrawerHTML) {
		const quickBuyHTML = window.themeVariables.quickBuyHTML;
		const quickBuyDrawerHTML = window.themeVariables.quickBuyDrawerHTML;

		const spanContainer = document.createElement('div');
		spanContainer.innerHTML = quickBuyHTML;
		const originalSpan = spanContainer.querySelector('span.dynamic-quick-buy-button');

		if (!originalSpan) {
			return;
		}

		const drawersMap = new Map();
		let currentHoveredElement = null;


		function handleProductItemHover(productItem, originalSpan, quickBuyDrawerHTML, drawersMap) {
			const productId = productItem.dataset.productId;
			if (!productId) {
				return;
			}

			const firstChild = productItem.firstElementChild;
			if (!firstChild) {
				return;
			}

			const existingSpan = firstChild.querySelector('span.dynamic-quick-buy-button[aria-controls]');
			if (existingSpan) {
				return; // Don't add again if it already exists
			}

			const existingQuickBuy = document.querySelector('span.dynamic-quick-buy-button[aria-controls]');
			if (existingQuickBuy) {
				existingQuickBuy.remove();
			}

			const quickBuySpan = document.createElement('span');

			Array.from(originalSpan.attributes).forEach(attr => {
				quickBuySpan.setAttribute(attr.name, attr.value);
			});

			quickBuySpan.innerHTML = originalSpan.innerHTML;
			quickBuySpan.className = originalSpan.className;

			if (!quickBuySpan.classList.contains('dynamic-quick-buy-button')) {
				quickBuySpan.classList.add('dynamic-quick-buy-button');
			}

			quickBuySpan.setAttribute('aria-controls', `product-${productId}`);

			quickBuySpan.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				e.stopImmediatePropagation();
				const drawer = document.querySelector(`quick-buy-drawer[id="product-${productId}"]`);
				if (drawer) {
					// Use the open property setter which triggers _load() before opening
					drawer.open = true;
				}
			}, true); // Use capture phase to intercept before other handlers

			setTimeout(() => {
				const currentFirstChild = productItem.firstElementChild;
				if (!currentFirstChild) {
					return;
				}

				// Double-check no span exists (in case it was added during timeout)
				const checkSpan = currentFirstChild.querySelector('span.dynamic-quick-buy-button[aria-controls]');
				if (!checkSpan) {
					currentFirstChild.appendChild(quickBuySpan);

					// Handle drawer - create or get existing one for this product
					if (!drawersMap.has(productId)) {
						// Find product URL from anchor tag with /products/ in href
						const productLink = productItem.querySelector('a[href*="/products/"]');
						let productUrl = '';

						if (productLink) {
							productUrl = productLink.getAttribute('href');
						}

						const drawerContainer = document.createElement('div');
						drawerContainer.innerHTML = quickBuyDrawerHTML;
						const drawer = drawerContainer.querySelector('quick-buy-drawer');

						if (drawer) {
							// Replace id with product ID
							drawer.setAttribute('id', `product-${productId}`);

							// Set href with product URL + view parameter
							if (productUrl) {
								const separator = productUrl.includes('?') ? '&' : '?';
								drawer.setAttribute('href', `${productUrl}${separator}view=quick-buy-drawer`);
							}

							// Append drawer to body (not inside product item) to avoid stacking context issues
							document.body.appendChild(drawer);

							drawersMap.set(productId, drawer);
						}
					}
				}
			}, 10);
		}


		// Use mousemove with event delegation to track cursor position
		document.addEventListener('mousemove', (event) => {
			const productItem = event.target.closest('.shopify-app-block .boost-sd__product-item');

			if (productItem) {
				// Only process if we're hovering a different element than before
				if (productItem !== currentHoveredElement) {
					currentHoveredElement = productItem;
					handleProductItemHover(productItem, originalSpan, quickBuyDrawerHTML, drawersMap);
				}
			} else {
				if (currentHoveredElement !== null) {
					currentHoveredElement = null;
				}
			}
		});
	}
})

function watchZeroPrices() {
	const PRICE_SELECTOR = ".boost-sd__format-currency";      // <-- change to your price element selector
	const ZERO_TEXTS = new Set(["$0", "$0.00", "0", "0.00"]); // <-- adapt as needed

	function normalizeText(t) {
		return t.replace(/\s+/g, " ").trim();
	}

	function updatePriceEl(el) {
		const txt = normalizeText(el.textContent || "");
		const isZero = ZERO_TEXTS.has(txt);

		el.classList.toggle("boost-sd__format-currency--is-zero-price", isZero);
	}

	// Initial pass (in case some are already present)
	document.querySelectorAll(PRICE_SELECTOR).forEach(updatePriceEl);

	const observer = new MutationObserver((mutations) => {
		for (const m of mutations) {
			// If a price node's text changed
			if (m.type === "characterData") {
				const priceEl = m.target.parentElement?.closest?.(PRICE_SELECTOR);
				if (priceEl) updatePriceEl(priceEl);
				continue;
			}

			// If nodes were added/changed in the DOM
			if (m.type === "childList") {
				// Check changed subtree for price elements
				m.addedNodes.forEach((node) => {
					if (node.nodeType !== 1) return; // element nodes only
					const el = node;

					if (el.matches?.(PRICE_SELECTOR)) updatePriceEl(el);
					el.querySelectorAll?.(PRICE_SELECTOR).forEach(updatePriceEl);
				});

				// Also handle cases where text nodes were swapped inside existing prices
				const targetEl = m.target.nodeType === 1 ? m.target : null;
				if (targetEl) {
					const maybePrices = targetEl.matches?.(PRICE_SELECTOR)
						? [targetEl]
						: targetEl.querySelectorAll?.(PRICE_SELECTOR) || [];
					maybePrices.forEach(updatePriceEl);
				}
			}
		}
	});

	observer.observe(document.documentElement, {
		subtree: true,
		childList: true,
		characterData: true
	});
}

watchZeroPrices();